import { getIdToken, refreshIdToken } from '../features/auth/descope';
import { decodeJwtClaims, parseViewingAs } from '@/features/viewAs/viewAs';
import { slackConnectErrorMessage } from '@/features/slackConnect/slackConnect';

// Thin client for the VisionHeight FastAPI backend. The browser only ever
// talks to this backend (it holds ANTHROPIC_API_KEY / VH_API_KEY).
//
// In DEV, BASE_URL is "" → requests go to a relative `/api/...` path that Vite
// proxies to the backend (same-origin, no CORS — see vite.config server.proxy).
// In PRODUCTION, set VITE_AGENT_BACKEND_URL to the backend's absolute origin.
const BASE_URL =
  (import.meta.env.VITE_AGENT_BACKEND_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/** Methods a view-as session may use. Mirrors the server's own rule in
 *  `auth/web_auth.py`, down to the single exemption: leaving is a POST. */
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const VIEW_AS_EXIT_PATH = '/api/view-as/stop';

/** Thrown instead of sending a write from a view-as session. Its own type so a
 *  caller can tell "the rule stopped this" from "the network did" — the generic
 *  copy most call sites show would otherwise blame the connection for a rule. */
export class ReadOnlySessionError extends Error {
  constructor() {
    super('Support view is read-only.');
    this.name = 'ReadOnlySessionError';
  }
}

/**
 * Does the view-as rule refuse this request? Exported so the rule itself can be
 * pinned by a test; `bfetch` is the only caller.
 *
 * Deliberately keyed on the METHOD rather than a list of endpoints, exactly as
 * the server does: every write here is a POST/PATCH/DELETE, so a call added
 * later is refused by default instead of quietly becoming another hole.
 */
export function refusedByReadOnly(
  method: string | undefined,
  path: string,
  claims: unknown,
): boolean {
  if (READ_METHODS.has((method ?? 'GET').toUpperCase())) return false;
  if (path === VIEW_AS_EXIT_PATH) return false; // leaving is the one allowed write
  return parseViewingAs(claims);
}

/**
 * Backend fetch: prefix BASE_URL and attach the Descope session JWT as
 * `Authorization: Bearer` when we have one (inert until the backend enforces
 * AUTH_REQUIRED). Direct-to-S3 presigned URLs (FilePreviewPanel, downloads) keep
 * using raw `fetch` — they must NOT carry our token.
 */
async function bfetch(path: string, init: RequestInit = {}): Promise<Response> {
  // One rule, at the one place every /api call passes, mirroring the server
  // instead of trusting each screen to remember. Hiding a control is still worth
  // doing — a dead button is bad UI — but it is not what makes this true: this
  // PR shipped with three live write paths (the result panel's query bar, the
  // drop zone, the live-offer buttons) that every per-screen gate had missed.
  if (refusedByReadOnly(init.method, path, decodeJwtClaims(getIdToken()))) {
    throw new ReadOnlySessionError();
  }
  const send = (token: string) => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(BASE_URL + path, { ...init, headers });
  };
  const res = await send(getIdToken());
  if (res.status !== 401) return res;
  // The SDK refreshes the JWT on its own timer, so a request sent in the gap
  // between expiry and refresh gets a 401 that isn't the user's fault. Renew
  // and retry ONCE; if the renewal fails the session really is over, and the
  // original 401 goes back to the caller. Bodies here are strings/FormData
  // (never streams), so resending the same init is safe.
  const fresh = await refreshIdToken();
  if (!fresh) return res;
  return send(fresh);
}

/** One conversation in the sidebar list (mirrors the backend SessionSummary). */
export interface SessionSummary {
  id: string;
  title: string;
  /** Is `title` still the backend's placeholder rather than a real name? The
   *  placeholder ("New investigation") is an ordinary string, so nothing else
   *  distinguishes it from a name the user typed — without this the sidebar
   *  rendered it as a title after a mid-turn refresh, where a shimmer belongs. */
  title_pending?: boolean;
  mode?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** The case this conversation belongs to, or null/undefined if ungrouped. */
  case_id?: string | null;
  detection_id?: string | null;
}

/** A file attached to a turn — an upload (user turn) or a generated file
 *  (agent turn). Rendered as a chip; downloadable when it has an s3 mirror. */
export interface Attachment {
  name: string;
  file_id?: string | null;
  size_bytes?: number;
  downloadable?: boolean;
}

/** One tag chip on an intel card. `tooltip` is the tag's description (hover). */
export interface IntelTag {
  label: string;
  tooltip?: string | null;
}

/**
 * The structured IP intel card the agent emits on a lookup turn. The client
 * renders this verbatim (dumb contract) — the agent builds every field
 * deterministically from the profile JSON except `overview`, which the LLM
 * writes. `currentRisk`: 0 unrated / 1 suspicious / 2 high.
 */
export interface IntelCard {
  /** The looked-up IP or domain (the card's subject). */
  value: string;
  companyName?: string | null;
  /** ISO alpha-3 country code (e.g. "USA"); the client maps it to a flag. */
  countryCode?: string | null;
  /** ISO alpha-3 codes for a domain (usageCountries); the client maps each to a flag. */
  countryCodes?: string[] | null;
  currentRisk: 0 | 1 | 2;
  tags: IntelTag[];
  /** Domain WHOIS creation date, raw "yyyy-MM-dd HH:mm:ss"; the client formats it. */
  whoisCreationDate?: string | null;
  /** Domain is on VisionHeight's Halo list. */
  isHalo?: boolean;
  /** One-paragraph LLM-written summary. */
  overview: string;
}

/**
 * Every card on a turn, whichever field carried it.
 *
 * One shape for the whole client: the wire has both `intel_cards` (current) and
 * `intel_card` (the first of them, kept for an un-updated client and for history
 * rows stored before the list existed), and nothing downstream should have to
 * know which one it got.
 */
export function intelCardsOf(m: {
  intel_card?: IntelCard | null;
  intel_cards?: IntelCard[] | null;
}): IntelCard[] {
  // Shape-checked, not just truthy-checked. Transcript JSON reaches this cast from
  // the wire with no runtime validation, and this is the boundary that is supposed
  // to absorb that: `intel_cards: "nope"` used to be returned AS the string, whose
  // `.map` then took the whole conversation down with it, and a null inside the list
  // reached IntelCard, which reads `card.value`.
  const list = Array.isArray(m.intel_cards)
    ? m.intel_cards.filter((c): c is IntelCard => c != null && typeof c === "object")
    : [];
  if (list.length > 0) return list;
  const one = m.intel_card;
  return one != null && typeof one === "object" ? [one] : [];
}

/** A chat message rendered in the conversation view. */
/** One interim narration line — the agent thinking out loud between tool calls,
 *  on the way to the answer. Rendered as a collapsible timeline above the answer
 *  bubble, never as a bubble of its own. */
export interface Thought {
  text: string;
  /** epoch-ms */
  timestamp: number;
  /** "narration" = the agent's own words; "action" = a step it took (a lookup,
   *  a check), in analyst terms. Rendered differently. Absent = narration. */
  kind?: 'narration' | 'action';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'agent';
  text: string;
  /** The agent's interim reasoning for THIS answer, oldest first. Empty for user
   *  turns and for answers reached in a single step. */
  thoughts?: Thought[];
  /** vh-viz-1 visualization payload, when the agent produced a chart. */
  visualization?: unknown;
  /** The first subject's card. Still on the wire so an un-updated client keeps
   *  working; INSIDE the client read `intel_cards` — use `intelCardsOf`. */
  intel_card?: IntelCard | null;
  /** One card per subject the turn carded. Asked about two indicators, the agent
   *  emits a card each; the server used to collapse them to the last one. */
  intel_cards?: IntelCard[] | null;
  /** Files shown as chips under the bubble: uploads on user turns, generated
   *  files on agent turns. */
  attachments?: Attachment[];
  /** When the message was sent, epoch-ms. */
  timestamp?: number;
}

/** The terminal `done` frame of a streaming turn. */
export interface DoneFrame {
  session_id: string;
  title?: string | null;
  answer: string;
  visualization?: unknown;
  intel_card?: IntelCard | null;
  intel_cards?: IntelCard[] | null;
  warnings?: string[];
  generated_files?: { file_id: string; filename: string; size_bytes: number }[];
}

interface StreamCallbacks {
  onStatus?: (text: string) => void;
  /** The session id, sent up front (before the title) so a new chat's row is
   *  keyed + clickable immediately. */
  onSession?: (sessionId: string) => void;
  /**
   * The server's id for THIS turn, sent the moment the message reached the agent
   * (after `session`, before any narration). The Stop button sends it back.
   *
   * Why an id at all: Stop used to say only "stop this chat". A Stop that took a
   * second to arrive — the previous turn finishing on its own meanwhile, the
   * user's next question already sent — then stopped the NEXT question, which
   * nobody had asked to stop. Naming the turn makes a late Stop a no-op instead.
   */
  onTurnStarted?: (turnId: string) => void;
  /** The cheap-model title streamed back early (parallel with the turn). */
  onTitle?: (title: string, sessionId: string) => void;
  /** One interim narration line, as the agent produces it — accumulate these
   *  into the in-flight answer's thought timeline. */
  onThought?: (thought: Thought) => void;
  onDone?: (frame: DoneFrame) => void;
  /** `kind` is the server's machine-readable tag on the error frame. Today the
   *  only value is 'model_outage' — the session's model is down but a NEW chat
   *  works (the backend creates it on a fallback model), so the UI offers one.
   *  Absent on generic errors and on client-side failures (network, HTTP). */
  onError?: (message: string, kind?: 'model_outage') => void;
  /**
   * The stream ended without a `done` or `error` frame — the connection went,
   * not the turn. The backend runs the turn off the response, so it is still
   * going (or already finished) on the server; the caller's job is to go and
   * read the transcript, NOT to tell the user their question failed.
   */
  onInterrupted?: () => void;
  /** Fired on ANY bytes off the wire — a real frame OR the server's keep-alive
   *  newline. It carries no content; it only says the stream is still alive, so a
   *  silently-dead stream (a torn-down page, a proxy holding a dead socket) can be
   *  told apart from a merely quiet turn and reconciled from the transcript. */
  onActivity?: () => void;
  signal?: AbortSignal;
}

/**
 * POST /api/sessions — start a conversation WITHOUT running an agent turn.
 *
 * The lookup gate: asking about an indicator is answered by the result panel in
 * well under a second, so the agent turn waits for the user to say yes. The
 * conversation still has to exist while they decide — that is the sidebar row,
 * its shimmer, the generated name and the id the URL comes back through — and
 * `message` is what gets titled. It is never sent to the agent from here.
 */
export async function openSession(
  message: string,
  mode: string,
  caseId: string | null,
  detectionId: string | null = null,
): Promise<string> {
  const res = await bfetch(`/api/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // detection_id: a chat opened from a detection link is ABOUT that detection
    // from its first moment — the server saves it on the chat, like case_id.
    body: JSON.stringify({
      message,
      mode,
      case_id: caseId ?? undefined,
      detection_id: detectionId ?? undefined,
    }),
  });
  if (!res.ok) throw new Error(`Failed to start the conversation (HTTP ${res.status})`);
  const data = (await res.json()) as { session_id?: string };
  if (!data.session_id) throw new Error('The server started no conversation.');
  return data.session_id;
}

/** GET /api/sessions — this app's conversations, newest first. */
export async function listSessions(signal?: AbortSignal): Promise<SessionSummary[]> {
  const res = await bfetch(`/api/sessions`, { signal });
  if (!res.ok) throw new Error(`Failed to load conversations (HTTP ${res.status})`);
  const data = (await res.json()) as { sessions?: SessionSummary[] };
  return Array.isArray(data.sessions) ? data.sessions : [];
}

/** A conversation as the server sees it right now. */
export interface Transcript {
  messages: ChatMessage[];
  /**
   * Is a turn still outstanding for this session?
   *
   * True while the last thing said is the user's — the server derives it from
   * the transcript, so it flips exactly once and reads the same from any worker.
   * The turn runs on the backend, off the request that started it, so this stays
   * true after the browser that started it is gone: that is what lets a refresh
   * (or a second tab) put the spinner back and wait for the answer instead of
   * rendering a dead conversation.
   */
  running: boolean;
}

/** GET /api/sessions/{id}/messages — reconstruct one transcript. */
export async function getSessionMessages(
  sessionId: string,
  signal?: AbortSignal,
): Promise<Transcript> {
  const res = await bfetch(
    `/api/sessions/${encodeURIComponent(sessionId)}/messages`,
    { signal },
  );
  if (!res.ok) throw new Error(`Failed to load conversation (HTTP ${res.status})`);
  const data = (await res.json()) as { messages?: ChatMessage[]; running?: boolean };
  const rows = Array.isArray(data.messages) ? data.messages : [];
  return {
    messages: rows.map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text,
      visualization: m.visualization ?? null,
      // Normalized here so the view never sees the two-field wire shape.
      intel_cards: intelCardsOf(m),
      attachments: Array.isArray(m.attachments) ? m.attachments : [],
      thoughts: Array.isArray(m.thoughts) ? m.thoughts : [],
      timestamp: m.timestamp,
    })),
    running: data.running === true,
  };
}

/**
 * POST /api/sessions/{id}/interrupt — the Stop button. Stops the agent itself,
 * not just this browser's listening: before this existed, Stop only aborted the
 * fetch, the turn ran on (and cost) in the background, and the composer opened
 * up while it did — so the next question started a second turn on a session that
 * was still busy, which is how one chat ran every tool call twice (VIS-580).
 *
 * `turnId` is the id from the stream's `turn_started` frame. With it, a Stop for
 * a turn that has already ended is ignored server-side, so it can never hit the
 * user's next question. `null` is for a chat we are only following by polling
 * (after a reload there is no stream, so no id): the server then stops whatever
 * turn holds the chat — which, for a chat showing "working", is the right one.
 *
 * 204 covers "nothing was running" too (Stop as the answer lands, or twice), so
 * only a real failure throws — and that one matters: the agent is still running,
 * and the caller must not tell the user it stopped.
 */
export async function interruptTurn(sessionId: string, turnId: string | null): Promise<void> {
  const res = await bfetch(`/api/sessions/${encodeURIComponent(sessionId)}/interrupt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(turnId ? { turn_id: turnId } : {}),
  });
  if (!res.ok) throw await detailError(res, 'Failed to stop');
}

/**
 * DELETE /api/sessions/{id} — archive (soft-delete) a conversation. One-way on
 * the backend (no unarchive); it just drops out of the sidebar list. A 404 means
 * it's already gone, which we treat as success (idempotent).
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const res = await bfetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to delete conversation (HTTP ${res.status})`);
  }
}

/** The CARD a question is about, named by the one key the server resolves under
 *  the verified account: a brand-impersonation row by its domain, an
 *  attack-surface finding by its id. `field` is the body field the key travels
 *  in — one per card kind, so the server never has to guess which table a key
 *  belongs to. Only the key leaves the browser, never the row. */
export interface Card {
  field: 'impersonation_domain' | 'attack_surface_finding_id';
  key: string;
}

/**
 * POST /api/message/stream — send a turn and consume the NDJSON event stream.
 * Managed Agents deliver whole messages (no token deltas), so `status` frames
 * narrate progress and the full answer rides in the terminal `done` frame.
 */
export async function sendMessageStream(
  sessionId: string | null,
  message: string,
  mode: string,
  cb: StreamCallbacks,
  caseId?: string | null,
  insightId?: string | null,
  card?: Card | null,
): Promise<void> {
  let res: Response;
  try {
    res = await bfetch(`/api/message/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // case_id only matters when creating a NEW session; the backend ignores
      // it for follow-ups. Omit it when null so the body stays clean.
      // insight_id names the card the question is about. It is an ID and nothing
      // more — the server reads the card out of its own table, so no title,
      // description or detection id is ever sent from here.
      // A place's card (`Card`) is the same contract: the key names the row, and
      // the server resolves it under the verified account. Where they differ is
      // on the way IN — the insight card is appended to the message text, a
      // place's card arrives beside it as its own `document` block, so nothing of
      // it can read as something the user wrote. Spelled out per field rather
      // than spread, so a grep for the wire name finds this.
      body: JSON.stringify({
        session_id: sessionId,
        message,
        mode,
        case_id: caseId ?? undefined,
        insight_id: insightId ?? undefined,
        impersonation_domain: card?.field === 'impersonation_domain' ? card.key : undefined,
        attack_surface_finding_id: card?.field === 'attack_surface_finding_id' ? card.key : undefined,
      }),
      signal: cb.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    // The connection died before the response even started. Creating the session
    // takes the server ~2s, so on a quick refresh THIS is where the dying page's
    // fetch rejects — not mid-stream. It says nothing about the turn (the server
    // processes the request regardless), so it must go down the same road as a
    // mid-stream drop: reporting it as an error ran the cleanup that deletes the
    // pending-turn note the reloaded page needs, which made recovery a coin toss
    // between this catch and the stream one.
    if (cb.onInterrupted) cb.onInterrupted();
    else cb.onError?.(e instanceof Error ? e.message : 'Network error reaching the backend');
    return;
  }
  if (!res.ok || !res.body) {
    // The backend puts the real reason in FastAPI's `detail` (e.g. "VH_API_KEY
    // unavailable", a missing CLAUDE_AGENT_ID) — surface it so a 503/500 is
    // actionable instead of a bare status code.
    let detail = '';
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      /* non-JSON / empty body — keep the bare status */
    }
    cb.onError?.(`Server error (HTTP ${res.status})${detail ? ` — ${detail}` : ''}`);
    return;
  }

  let terminal = false; // saw a done/error frame (a clean end)
  const handle = (line: string) => {
    // A callback can abort this stream from INSIDE the loop — a held Stop goes
    // out the moment `turn_started` lands, and aborts on its way. The reader has
    // usually read further than that frame by then: one network read can carry
    // several frames when the server flushes them together (a short answer's
    // `turn_started` + `done`, say), and every one of them would still be handled
    // here — with the hold already gone, so a `done` painted the answer over
    // "Response stopped" and an `error` put an error under a turn the user had
    // stopped. Aborted means nobody is listening: drop the rest of the chunk.
    if (cb.signal?.aborted) return;
    if (!line.trim()) return;
    let frame: { type?: string; text?: string; message?: string; timestamp?: number; kind?: string; turn_id?: string } & Partial<DoneFrame>;
    try {
      frame = JSON.parse(line);
    } catch {
      return; // skip a malformed line
    }
    if (frame.type === 'session' && typeof frame.session_id === 'string')
      cb.onSession?.(frame.session_id);
    else if (frame.type === 'turn_started' && typeof frame.turn_id === 'string')
      cb.onTurnStarted?.(frame.turn_id);
    else if (frame.type === 'status' && typeof frame.text === 'string') cb.onStatus?.(frame.text);
    else if (frame.type === 'title' && typeof frame.title === 'string')
      cb.onTitle?.(frame.title, frame.session_id ?? '');
    else if (frame.type === 'thought' && typeof frame.text === 'string')
      cb.onThought?.({
        text: frame.text,
        timestamp: frame.timestamp ?? Date.now(),
        kind: frame.kind === 'action' ? 'action' : 'narration',
      });
    else if (frame.type === 'error') {
      terminal = true;
      cb.onError?.(
        frame.message || 'The agent hit an error',
        frame.kind === 'model_outage' ? 'model_outage' : undefined,
      );
    } else if (frame.type === 'done') {
      terminal = true;
      cb.onDone?.(frame as DoneFrame);
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      cb.onActivity?.(); // bytes arrived (a frame or a keep-alive) → the stream lives
      buf += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0) {
        handle(buf.slice(0, nl));
        buf = buf.slice(nl + 1);
      }
    }
    if (buf.trim()) handle(buf);
    // Stream closed without a done/error frame — the backend restarted, a proxy
    // cut an idle connection, the laptop slept. This used to be reported as an
    // error, which put "the connection closed" over a turn that was completing
    // normally on the server. It is a lost READER, so say that and let the caller
    // re-read the transcript.
    if (!terminal && !cb.signal?.aborted) cb.onInterrupted?.();
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    // Same reasoning: a mid-read network failure says nothing about the turn.
    if (!terminal) cb.onInterrupted?.();
  }
}

// ---------------------------------------------------------------------------
// Files: upload + download
// ---------------------------------------------------------------------------

export interface UploadedFile {
  file_id: string;
  filename: string;
  mount_path: string;
  size_bytes: number;
}
export interface UploadResult {
  session_id: string;
  files: UploadedFile[];
}

/** Client-side soft caps; the backend enforces the hard limits and returns 4xx. */
export const MAX_UPLOAD_FILES = 10;
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * POST /api/upload — upload + mount files into the session (creates one if
 * `sessionId` is null). Don't set Content-Type — the browser adds the
 * multipart boundary. Returns the canonical file ids + mount paths.
 */
export async function uploadFiles(files: File[], sessionId: string | null): Promise<UploadResult> {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  if (sessionId) form.append('session_id', sessionId);

  const res = await bfetch(`/api/upload`, { method: 'POST', body: form });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = body;
    try {
      detail = (JSON.parse(body) as { detail?: string }).detail ?? body;
    } catch {
      /* not JSON — use raw text */
    }
    throw new Error(`Upload failed (HTTP ${res.status})${detail ? ` — ${detail}` : ''}`);
  }
  return (await res.json()) as UploadResult;
}

/**
 * GET /api/files/{id}/download[?inline] — mint a short-lived presigned S3 URL.
 * `inline=true` returns an inline-disposition URL the preview panel can render
 * (image src / pdf iframe / fetched text); the default forces a download.
 */
export async function getFileUrl(fileId: string, inline = false): Promise<string> {
  const qs = inline ? '?inline=true' : '';
  const res = await bfetch(`/api/files/${encodeURIComponent(fileId)}/download${qs}`);
  if (!res.ok) {
    if (res.status === 410) throw new Error('This file predates the download mirror and is no longer available.');
    if (res.status === 404) throw new Error('This file is no longer available.');
    throw new Error(`Could not load file (HTTP ${res.status})`);
  }
  const { url } = (await res.json()) as { url: string };
  return url;
}

/** Mint a download URL and trigger the browser download via a transient
 *  `<a download>`. The S3 Content-Disposition forces a save dialog. */
export async function downloadFile(fileId: string, filename: string): Promise<void> {
  const url = await getFileUrl(fileId, false);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename; // hint; S3's Content-Disposition is authoritative
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ---------------------------------------------------------------------------
// Cases: named groups of conversations
// ---------------------------------------------------------------------------

/** A case (folder) grouping conversations (mirrors the backend CaseOut). */
export interface Case {
  case_id: string;
  name: string;
  created_at: number; // epoch seconds
  updated_at: number; // epoch seconds
}

/** GET /api/cases — the user's cases, newest first. */
export async function listCases(signal?: AbortSignal): Promise<Case[]> {
  const res = await bfetch(`/api/cases`, { signal });
  if (!res.ok) throw new Error(`Failed to load cases (HTTP ${res.status})`);
  const data = (await res.json()) as { cases?: Case[] };
  return Array.isArray(data.cases) ? data.cases : [];
}

export interface SlackConnection {
  slack_workspace_id: string;
  slack_workspace_name: string;
}

/** POST /api/slack/connected — record the Slack workspace the signed-in user just
 *  approved. No body: the server takes the organization and email from the JWT. */
export async function connectSlack(): Promise<SlackConnection> {
  let res: Response;
  try {
    res = await bfetch(`/api/slack/connected`, { method: 'POST' });
  } catch (e) {
    // The read-only rule says why itself; a dropped connection would otherwise
    // surface as the browser's "Failed to fetch".
    if (e instanceof ReadOnlySessionError) throw e;
    throw new Error(slackConnectErrorMessage(0));
  }
  if (!res.ok) throw new Error(slackConnectErrorMessage(res.status));
  try {
    return (await res.json()) as SlackConnection;
  } catch {
    // A 2xx that isn't our JSON (e.g. a proxy's HTML page) would otherwise show
    // the parser's "Unexpected token <".
    throw new Error(slackConnectErrorMessage(0));
  }
}

/** POST /api/cases — create a new (empty) case. */
export async function createCase(name: string): Promise<Case> {
  const res = await bfetch(`/api/cases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Failed to create case (HTTP ${res.status})`);
  return (await res.json()) as Case;
}

/** PATCH /api/cases/{id} — rename a case. */
export async function renameCase(caseId: string, name: string): Promise<void> {
  const res = await bfetch(`/api/cases/${encodeURIComponent(caseId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Failed to rename case (HTTP ${res.status})`);
}

/** DELETE /api/cases/{id} — delete a case AND archive the conversations inside
 *  it (the backend cascades: "a deleted case takes its sessions with it").
 *  404 means it's already gone, treated as success (idempotent). */
export async function deleteCase(caseId: string): Promise<void> {
  const res = await bfetch(`/api/cases/${encodeURIComponent(caseId)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) throw new Error(`Failed to delete case (HTTP ${res.status})`);
}

/** PATCH /api/sessions/{id} — move a conversation into a case (caseId), or out
 *  of any case (caseId = null). */
export async function setSessionCase(sessionId: string, caseId: string | null): Promise<void> {
  const res = await bfetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ case_id: caseId }),
  });
  if (!res.ok) throw new Error(`Failed to update conversation (HTTP ${res.status})`);
}

/** One day of the caller's own usage. `agents` maps an agent name to
 *  `[tokens_in, tokens_out]` — a MAP because the agent set is data, not schema:
 *  the usage Lambda invents a column the first time an agent runs, so a new
 *  agent has to reach the chart with no release on either side. RAW tokens. */
export interface UsageDay {
  date: string; // "YYYY-MM-DD" (UTC)
  agents: Record<string, [number, number]>;
}

/** One month of the ACCOUNT's usage — WEIGHTED tokens (raw × per-agent scalar),
 *  i.e. the same numbers the licence gate compares. */
export interface UsageMonth {
  month: string; // "YYYY-MM"
  tokens_in: number;
  tokens_out: number;
}

/** The licence bars: WEIGHTED tokens used against the tenant's ceiling,
 *  cumulative over the whole contract (not the month) — that's what the gate
 *  sums. A null limit means this deployment has no licence configured, and the
 *  UI must say "no limit" instead of drawing a 0% bar. */
export interface UsageLicence {
  tokens_in: number;
  tokens_out: number;
  limit_in: number | null;
  limit_out: number | null;
  contract_end_ms: number | null;
}

/** Everything the Usage screen draws, for the caller only. Two sources on
 *  purpose: `days` is per-user RAW, `months`/`licence` are per-account WEIGHTED.
 *  Mixing them would be wrong by the per-agent scalar (hunter counts 5×), so
 *  they stay apart all the way to the screen. */
export interface Usage {
  month: string; // the month `days` covers, "YYYY-MM"
  days: UsageDay[];
  months: UsageMonth[];
  licence: UsageLicence;
}

/** GET /api/usage — the caller's usage for one month (defaults to the current
 *  UTC month server-side) plus the account's whole licence history. */
export async function fetchUsage(month?: string, signal?: AbortSignal): Promise<Usage> {
  const res = await bfetch(`/api/usage${month ? `?month=${encodeURIComponent(month)}` : ''}`, {
    signal,
  });
  if (!res.ok) throw await detailError(res, 'Failed to load usage');
  return (await res.json()) as Usage;
}

export interface ApiKey {
  id: string;
  name: string;
  created: number; // epoch seconds
  /** Epoch seconds; 0 = never expires. Descope keeps calling an expired key
   *  "active", so `active` alone doesn't tell you the key still works. */
  expires: number;
  active: boolean;
  /** False for a key minted before the server stamped its owner's id on it: it
   *  authenticates as itself rather than as you, so the UI has to flag it. */
  bound: boolean;
}

/** The backend's own `detail`, when it has one. The 501 it sends in demo mode
 *  and the 409 at the per-user ceiling both explain themselves, where a bare
 *  status code would just send you to the logs. */
async function detailError(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { detail?: string } | null;
  return new Error(body?.detail || `${fallback} (HTTP ${res.status})`);
}

/** GET /api/api-key — the user's API keys, newest first. Metadata only: the
 *  secret exists in one response only, the POST that created it. */
export async function listApiKeys(signal?: AbortSignal): Promise<ApiKey[]> {
  const res = await bfetch(`/api/api-key`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load API keys');
  const data = (await res.json()) as { keys?: ApiKey[] };
  return Array.isArray(data.keys) ? data.keys : [];
}

/** POST /api/api-key — mint a new API key. The cleartext comes back ONCE
 *  (Descope only reveals it here), so the caller must show it and must not
 *  refetch on a remount. Existing keys keep working.
 *
 *  `expireDays` is a lifetime, not a deadline: the server turns it into an
 *  absolute time off its own clock, so this machine's clock can't mint a key
 *  that's already dead. `null` = never expires. */
export async function createApiKey(
  name: string,
  expireDays: number | null = null,
): Promise<{ id: string; key: string }> {
  const res = await bfetch(`/api/api-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, expire_days: expireDays }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to create API key');
  const data = (await res.json()) as { id?: string; key?: string };
  if (!data.key) throw new Error('Failed to create API key (empty response)');
  return { id: data.id ?? '', key: data.key };
}

/** DELETE /api/api-key/{id} — revoke a key immediately. 404 means it's already
 *  gone (or was never yours), treated as success: either way it can't be used. */
export async function deleteApiKey(id: string): Promise<void> {
  const res = await bfetch(`/api/api-key/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) throw await detailError(res, 'Failed to delete API key');
}

/** PATCH /api/api-key/{id} — turn a key off, or back on. The reversible half of
 *  delete: the key stops authenticating at once but keeps existing, so a key that
 *  MIGHT be leaked can be parked instead of burned. */
export async function setApiKeyActive(id: string, active: boolean): Promise<void> {
  const res = await bfetch(`/api/api-key/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok)
    throw await detailError(res, `Failed to ${active ? 'activate' : 'deactivate'} API key`);
}

// ---------------------------------------------------------------------------
// View as customer: read-only, permission-gated, audited. See features/viewAs.
// ---------------------------------------------------------------------------

/** One customer in the view-as picker. */
export interface ViewAsTenant {
  id: string;
  name: string;
}

/** One person in the view-as picker. `login_id` is what the session is minted
 *  from; the rest is what the row shows. */
export interface ViewAsUser {
  user_id: string;
  login_id: string;
  email: string;
  name: string;
}

/** GET /api/view-as/tenants — every customer this admin may look at. A 403 means
 *  the permission isn't granted; the UI hides the feature rather than retrying. */
export async function listViewAsTenants(signal?: AbortSignal): Promise<ViewAsTenant[]> {
  const res = await bfetch(`/api/view-as/tenants`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load customers');
  const data = (await res.json()) as { tenants?: ViewAsTenant[] };
  return Array.isArray(data.tenants) ? data.tenants : [];
}

/** GET /api/view-as/tenants/{id}/users — one customer's people. */
export async function listViewAsUsers(
  tenantId: string,
  signal?: AbortSignal,
): Promise<ViewAsUser[]> {
  const res = await bfetch(
    `/api/view-as/tenants/${encodeURIComponent(tenantId)}/users`,
    { signal },
  );
  if (!res.ok) throw await detailError(res, 'Failed to load users');
  const data = (await res.json()) as { users?: ViewAsUser[] };
  return Array.isArray(data.users) ? data.users : [];
}

/** POST /api/view-as/start — mint the customer's session.
 *
 *  Returns a refresh JWT for `adoptSession` to swap in. Who is DOING the viewing
 *  is not sent: the server reads it off this request's own token. */
export async function startViewAs(loginId: string, tenantId: string): Promise<string> {
  const res = await bfetch(`/api/view-as/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login_id: loginId, tenant_id: tenantId }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to start viewing as this customer');
  const data = (await res.json()) as { refresh_jwt?: string };
  if (!data.refresh_jwt) throw new Error('No session came back from the server.');
  return data.refresh_jwt;
}

/** POST /api/view-as/stop — trade the view-as session back for the admin's own.
 *
 *  The one write a view-as session is allowed to make (see the server's
 *  read-only rule), because it is the way out. */
export async function stopViewAs(refreshJwt: string): Promise<string> {
  const res = await bfetch(`/api/view-as/stop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_jwt: refreshJwt }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to exit');
  const data = (await res.json()) as { refresh_jwt?: string };
  if (!data.refresh_jwt) throw new Error('No session came back from the server.');
  return data.refresh_jwt;
}

/** One card on the Insights screen (mirrors the backend `InsightOut`).
 *
 *  `type` is an OPEN subtype (`active-threat`, `pulse-insight`, `exposure`,
 *  `alert-hygiene`, `needs-decision`, …): the agent may mint a new value, so the UI
 *  formats whatever arrives instead of switching on a closed set. `risk` is
 *  Elliot's own assessment, not the vendor's severity. The evidence the insight was
 *  derived from stays server-side and is deliberately absent here. */
export interface Insight {
  id: string;
  title: string;
  description: string;
  type: string;
  risk: string; // critical | high | medium | low | info
  tags: string[];
  /** Which agent produced this card — `TRIAGE` today, one of a planned set. The
   *  card renders it Title Case; older rows default to `TRIAGE` server-side. */
  category: string;
}

/** GET /api/insights — the newest daily run for the signed-in account (the account
 *  comes from the token; there is no parameter to pass).
 *
 *  `generatedAt` is "" when the account has no insights yet, which is an empty
 *  screen and not an error. A failed lookup arrives as a 503 and throws — an
 *  unreachable table must never render as "no insights", i.e. as "all clear".
 *
 *  The thrown error carries `status`, because 401 is a DIFFERENT message: the
 *  screen's error copy says "this is not an all-clear", which is the right thing
 *  to say about an unreachable table and the wrong thing to say to someone whose
 *  session simply expired. Signing out and in is the fix for one and useless for
 *  the other. */
export async function listInsights(
  signal?: AbortSignal,
): Promise<{ generatedAt: string; insights: Insight[] }> {
  const res = await bfetch(`/api/insights`, { signal });
  if (!res.ok) {
    throw Object.assign(new Error(`Failed to load insights (HTTP ${res.status})`), {
      status: res.status,
    });
  }
  const data = (await res.json()) as { generated_at?: string; insights?: Insight[] };
  return {
    generatedAt: data.generated_at ?? '',
    insights: Array.isArray(data.insights) ? data.insights : [],
  };
}

/** PATCH /api/sessions/{id} — rename a conversation. */
export async function renameSession(sessionId: string, title: string): Promise<void> {
  const res = await bfetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(`Failed to rename conversation (HTTP ${res.status})`);
}

/** The agent's call on a lookalike domain. `need_action` is the one that gets a
 *  red chip and its own tab; everything else is context. */
export type AgentVerdict = 'need_action' | 'informational';

/** What the account has decided to DO about a domain. `unreviewed` is where every
 *  row starts — it is a state, not a choice (see DECISION_CHOICES). */
export type DecisionStatus = 'unreviewed' | 'ignore' | 'monitor' | 'mine' | 'takedown';

/** One row of the Brand impersonation table (wire shape, snake_case verbatim —
 *  the DynamoDB `elliot-impersonation-domains` record).
 *
 *  `first_seen_at` is unix SECONDS, not milliseconds like the Feeds timestamps.
 *  `evidence` is on the wire but empty everywhere today: `explanation` is the one
 *  text the agent writes, and the table renders that. */
export interface ImpersonationDomain {
  domain: string;
  agent_verdict: AgentVerdict;
  evidence: string;
  explanation: string;
  first_seen_at: number;
  user_decision_status: DecisionStatus;
  /** The hunt captured a screenshot of this domain — its page shows it, fetched
   *  through getImpersonationScreenshot (the S3 path never reaches the browser). */
  screenshot: boolean;
}

/** GET /api/impersonation/domains — every lookalike domain found for the signed-in
 *  account (the account comes from the token; there is no parameter to pass).
 *  The whole set arrives in one response — the table pages through it client-side. */
export async function listImpersonationDomains(
  signal?: AbortSignal,
): Promise<ImpersonationDomain[]> {
  const res = await bfetch(`/api/impersonation/domains`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load brand impersonation');
  const data = (await res.json()) as { domains?: Partial<ImpersonationDomain>[] };
  return Array.isArray(data.domains) ? data.domains.map(normalizeDomain) : [];
}

/** Pull one row onto the closed shape the screen renders.
 *
 *  Not defensive habit: the backend model defaults every one of these strings to
 *  "" (a row the hunt job has written but not yet verdicted), and "" is in neither
 *  union. Left alone it reaches the table as a chip with no label and a verdict
 *  box with nothing in it — two controls that read as broken rather than as "not
 *  looked at yet". An unrecognised value lands on the same safe floor. */
export function normalizeDomain(d: Partial<ImpersonationDomain>): ImpersonationDomain {
  return {
    domain: String(d.domain ?? ''),
    agent_verdict: d.agent_verdict === 'need_action' ? 'need_action' : 'informational',
    evidence: String(d.evidence ?? ''),
    explanation: String(d.explanation ?? ''),
    first_seen_at: Number(d.first_seen_at ?? 0),
    user_decision_status: DECISION_STATUSES.includes(d.user_decision_status as DecisionStatus)
      ? (d.user_decision_status as DecisionStatus)
      : 'unreviewed',
    // Only an explicit true: a server from before the field says nothing, and
    // nothing means no picture to ask for.
    screenshot: d.screenshot === true,
  };
}

/** Every value `user_decision_status` can hold on the wire (the DynamoDB
 *  attribute's documented set) — the guard for normalizeDomain, not a UI list;
 *  what the dropdown OFFERS is a shorter one (see DECISION_CHOICES). */
const DECISION_STATUSES: DecisionStatus[] = [
  'unreviewed',
  'ignore',
  'monitor',
  'mine',
  'takedown',
];

/** POST /api/impersonation/domains/decision — record what to do about one domain.
 *
 *  Answers 204: no body, so there is nothing to read back. `takedown` is interim
 *  server-side — a status write plus a best-effort team alert — which is another
 *  reason not to refetch for the outcome. The screen shows the new value
 *  optimistically and puts it back only if this throws.
 *
 *  The server accepts mine | ignore | monitor | takedown and REJECTS `unreviewed`
 *  with a 400 (see DECISION_CHOICES — the dropdown never offers it). A `mine`
 *  decision also drops the row from the next GET: owned domains are excluded. */
export async function setImpersonationDecision(
  domain: string,
  status: DecisionStatus,
): Promise<void> {
  const res = await bfetch(`/api/impersonation/domains/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain, status }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to save your verdict');
}

/** GET /api/impersonation/domains/{domain}/screenshot — a short-lived pre-signed
 *  URL to the screenshot the hunt captured (inline, for an <img>), and when it was
 *  captured (unix SECONDS; 0 if not recorded). Ask only for a row whose
 *  `screenshot` is true: the server answers 404 for the rest. */
export async function getImpersonationScreenshot(
  domain: string,
  signal?: AbortSignal,
): Promise<{ url: string; captured_at: number }> {
  const res = await bfetch(`/api/impersonation/domains/${encodeURIComponent(domain)}/screenshot`, {
    signal,
  });
  if (!res.ok) throw await detailError(res, "Couldn't load the screenshot");
  const data = (await res.json()) as { url?: string; captured_at?: number };
  return { url: String(data.url ?? ''), captured_at: Number(data.captured_at ?? 0) };
}

// --- attack surface: the tenant's external surface and the findings on it ----
// Wire shapes mirror elliot-server's `models.py` on feat/attack-surface-mapping
// (AssetOut / FindingOut), snake_case verbatim, times in epoch SECONDS.

/** Every row kind on the surface. The first five are ASSETS the tenant owns and
 *  may add or categorise by hand; the last three are OBSERVATIONS seen on an
 *  asset (a port, a DNS record, a CVE) and never added by hand. */
export type SurfaceType = 'domain' | 'subdomain' | 'ip' | 'cidr' | 'cert' | 'port' | 'dns' | 'cve';
const SURFACE_TYPES: readonly SurfaceType[] = ['domain', 'subdomain', 'ip', 'cidr', 'cert', 'port', 'dns', 'cve'];

/** One row of GET /api/attacksurface/assets (the server's AssetOut).
 *
 *  `customer_categories` are the applications the categorizer agent (or the
 *  client) filed the asset under; an observation carries its parent's copy.
 *  `linked_assets` is set on a cert only: the hosts / IPs it is served on.
 *  `parent` is set on a port / dns / cve only: the asset the scanner found it
 *  on — read off the wire, never out of the value (an IPv6 port has colons of
 *  its own). On the wire but not read here, because the screen draws none of
 *  them: `first_seen_at` / `last_seen_at`, a cve's `severity` / `cvss`. */
export interface SurfaceItem {
  type: SurfaceType;
  value: string;
  explanation: string;
  customer_categories: string[];
  linked_assets: string[];
  /** Domains, subdomains and IPs: VisionHeight's own tags on the asset as of the
   *  last scan that reported them — its VH tags plus each true flag as a short
   *  name (`is_scanner` → `scanner`). Empty for everything else (QA item 6). */
  tags: string[];
  parent: string;
}

/** GET /api/attacksurface/assets — the whole surface in one response, assets and
 *  observations alike, pre-sorted by category (the account comes from the token). */
export async function listSurface(signal?: AbortSignal): Promise<SurfaceItem[]> {
  const res = await bfetch(`/api/attacksurface/assets`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load the attack surface');
  const data = (await res.json()) as { assets?: Partial<SurfaceItem>[] };
  if (!Array.isArray(data.assets)) return [];
  return data.assets.map(normalizeSurfaceItem).filter((i): i is SurfaceItem => i != null);
}

/** Pull one row onto the closed shape, or null for a kind this client cannot
 *  draw. Dropping rather than flooring, because there is no "safe" kind to land
 *  an unknown row on — unlike a verdict, a type decides which table it goes in. */
export function normalizeSurfaceItem(d: Partial<SurfaceItem>): SurfaceItem | null {
  if (!SURFACE_TYPES.includes(d.type as SurfaceType)) return null;
  return {
    type: d.type as SurfaceType,
    value: String(d.value ?? ''),
    explanation: String(d.explanation ?? ''),
    customer_categories: Array.isArray(d.customer_categories) ? d.customer_categories.map(String) : [],
    linked_assets: Array.isArray(d.linked_assets) ? d.linked_assets.map(String) : [],
    tags: Array.isArray(d.tags) ? d.tags.map(String) : [],
    parent: String(d.parent ?? ''),
  };
}

/** The agent's call on how serious a finding is. Lowercase on the wire (the
 *  server's validator and sort both read it that way); the agent prompt spells
 *  it "High", so the reader lowercases rather than trusting either. */
export type Severity = 'high' | 'medium' | 'low';
/** Top first — the order the chips are drawn in and the order the server sorts by. */
export const SEVERITIES: readonly Severity[] = ['high', 'medium', 'low'];

/** A finding's lifecycle. The client never writes these directly — it sends an
 *  ACTION (resolved | ignore | unresolved; the writer arrives with the status
 *  increment) and the server moves the status: unresolved → (resolved)
 *  pending_verification → (next scan) resolved_verified or back to unresolved;
 *  (ignore) → ignored, which the agent re-checks weekly. */
export type FindingStatus = 'unresolved' | 'pending_verification' | 'resolved_verified' | 'ignored';
const FINDING_STATUSES: readonly FindingStatus[] = [
  'unresolved',
  'pending_verification',
  'resolved_verified',
  'ignored',
];

/** One evidence row: what fired and the state it was observed in. */
export interface FindingEvidence {
  driver: string;
  state: string;
}

/** One row of GET /api/attacksurface/findings (the server's FindingOut).
 *  `finding_id` = hash(asset + rule), stable across scans — the key every later
 *  write names. `resolved_at` / `verified_at` exist only once reached. */
export interface Finding {
  finding_id: string;
  asset: string;
  rule: string;
  title: string;
  what_it_is: string;
  summary: string;
  severity: Severity;
  priority: number;
  recommended_fix: string;
  evidence: FindingEvidence[];
  status: FindingStatus;
  discovery_time: number;
  last_check_time: number;
  resolved_at: number | null;
  verified_at: number | null;
}

/** GET /api/attacksurface/findings — every finding on the account, High first
 *  then newest. The endpoint takes `?severity=` / `?status=` but the screen
 *  filters client-side instead: one fetch, and the tab counts need the whole set. */
export async function listFindings(signal?: AbortSignal): Promise<Finding[]> {
  const res = await bfetch(`/api/attacksurface/findings`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load findings');
  const data = (await res.json()) as { findings?: Partial<Finding>[] };
  return Array.isArray(data.findings) ? data.findings.map(normalizeFinding) : [];
}

/** Pull one row onto the closed shape. The server defaults every string to ""
 *  (a row the job wrote before the agent filled it), and "" is in neither union:
 *  severity lands on `low` so the row still shows (and sorts last), status on
 *  `unresolved` — the server's own default for a missing one. */
export function normalizeFinding(d: Partial<Finding>): Finding {
  const sev = String(d.severity ?? '').toLowerCase() as Severity;
  const evidence = Array.isArray(d.evidence)
    ? d.evidence.map((e) => {
        // A string or a number spread as an object has neither key — it closes
        // to an empty row rather than throwing on `.driver`.
        const row = (e != null && typeof e === 'object' ? e : {}) as Partial<FindingEvidence>;
        return {
          driver: row.driver == null ? '' : String(row.driver),
          state: row.state == null ? '' : String(row.state),
        };
      })
    : [];
  const optionalTime = (v: unknown): number | null => (v == null ? null : Number(v));
  return {
    finding_id: String(d.finding_id ?? ''),
    asset: String(d.asset ?? ''),
    rule: String(d.rule ?? ''),
    title: String(d.title ?? ''),
    what_it_is: String(d.what_it_is ?? ''),
    summary: String(d.summary ?? ''),
    severity: SEVERITIES.includes(sev) ? sev : 'low',
    priority: Number(d.priority ?? 0),
    recommended_fix: String(d.recommended_fix ?? ''),
    evidence,
    status: FINDING_STATUSES.includes(d.status as FindingStatus) ? (d.status as FindingStatus) : 'unresolved',
    discovery_time: Number(d.discovery_time ?? 0),
    last_check_time: Number(d.last_check_time ?? 0),
    resolved_at: optionalTime(d.resolved_at),
    verified_at: optionalTime(d.verified_at),
  };
}

/** POST /api/attacksurface/assets/add — an asset the client typed in. The server
 *  checks the value really is the kind named (an IP is public, a range has a
 *  prefix, a hostname has a known TLD…) and answers 400 with the reason; on
 *  201 it answers the stored row, categories normalised (trimmed, lowercased). */
export async function addAsset(body: {
  type: SurfaceType;
  value: string;
  categories: string[];
}): Promise<SurfaceItem> {
  const res = await bfetch(`/api/attacksurface/assets/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await detailError(res, 'Failed to add the asset');
  const item = normalizeSurfaceItem((await res.json()) as Partial<SurfaceItem>);
  if (!item) throw new Error('The server answered with a row this screen cannot draw');
  return item;
}

/** What a person can DO to a finding — the body of POST /findings/status. The
 *  server moves the status: `resolved` → pending_verification, `ignore` →
 *  ignored, `unresolved` → unresolved (re-open). Severity is the agent's alone. */
export type FindingAction = 'resolved' | 'ignore' | 'unresolved';

/** POST /api/attacksurface/findings/status — apply one action to one finding.
 *
 *  Answers the updated finding (same shape as a GET row), so the screen replaces
 *  its optimistic row with the server's — that is where `resolved_at` comes
 *  from. 404 if the id is unknown (the scan dropped it since the list loaded). */
export async function setFindingStatus(findingId: string, action: FindingAction): Promise<Finding> {
  const res = await bfetch(`/api/attacksurface/findings/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ finding_id: findingId, status: action }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to update the finding');
  return normalizeFinding((await res.json()) as Partial<Finding>);
}

// --- members: a tenant admin manages their own org ---------------------------

/** One row of the Members screen (mirrors the backend MemberOut). `roles` are
 *  the caller's-tenant roles only; `status` is Descope's own word
 *  (enabled / invited / disabled), passed through unfiltered. */
export interface Member {
  user_id: string;
  login_id: string;
  email: string;
  name: string;
  status: string;
  roles: string[];
}

/** GET /api/users — everyone in the caller's org, plus the email domains an
 *  invite may target (the tenant's configured domains union the admin's own —
 *  the server computes it, the dialog only mirrors it). Manage Users only. */
export async function listMembers(
  signal?: AbortSignal,
): Promise<{ members: Member[]; allowedDomains: string[] }> {
  const res = await bfetch(`/api/users`, { signal });
  if (!res.ok) throw await detailError(res, 'Failed to load members');
  const data = (await res.json()) as { members?: Member[]; allowed_domains?: string[] };
  return {
    members: Array.isArray(data.members) ? data.members : [],
    allowedDomains: Array.isArray(data.allowed_domains) ? data.allowed_domains : [],
  };
}

/** POST /api/users — invite one address into the org with the given roles.
 *  Descope sends the invite mail. 409 when the address already has an account. */
export async function inviteMember(email: string, roles: string[]): Promise<void> {
  const res = await bfetch(`/api/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, roles }),
  });
  if (!res.ok) throw await detailError(res, 'Failed to send the invite');
}

/** DELETE /api/users/{login_id} — take one member out of the org. The server
 *  refuses your own account (removing yourself is a lockout, not an offboarding). */
export async function removeMember(loginId: string): Promise<void> {
  const res = await bfetch(`/api/users/${encodeURIComponent(loginId)}`, { method: 'DELETE' });
  if (!res.ok) throw await detailError(res, 'Failed to remove the member');
}

/** PATCH /api/users/{login_id} — replace one member's roles (a replacement, not
 *  a delta). The server refuses your own account. */
export async function setMemberRoles(loginId: string, roles: string[]): Promise<void> {
  const res = await bfetch(`/api/users/${encodeURIComponent(loginId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roles }),
  });
  if (!res.ok) throw await detailError(res, "Failed to change the member's roles");
}
